import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AccountType as CommonAccountType } from '@pepper-ui/common';
import { setAccountTypeSelected } from '../features/registration/registrationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../features/app/appSlice';
import EnvironmentWatermark from './installer/EnvironmentWatermark';
import {
  chooseAccountType,
  signInRegistration,
  getConfigurableDeadlines,
} from '../firebase';
import useGaAccountTypeStartEvent from '../hooks/useGaAccountTypeStartEvent';
import useTitle from '../hooks/useTitle';

const AccountType = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [hasError, setHasError] = useState(false);
  const [getAccountTypeSelectedLocal, setAccountTypeSelectedLocal] =
    useState('');
  const [landlord, setLandlord] = useState(true);
  const [smeCarParkClaims, setSmeCarParkClaims] = useState(true);
  const [pmcCarParkClaims, setPmcCarParkClaims] = useState(true);
  const [installerAccount, setInstallerAccount] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  useTitle(
    'SMEReg.AccountType.s15',
    'What type of account do you need? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  useGaAccountTypeStartEvent(getAccountTypeSelectedLocal);

  const legendTitle = copyProvider.getCopy(
    'SMEReg.AccountType.s1',
    'What type of account do you need?'
  );

  const errorMessage = copyProvider.getCopy(
    'SMEReg.AccountType.s2',
    'Select a User'
  );
  // check all grant in past dates
  const allGrantsInPastDates = (deadlines) => {
    if (deadlines.stopToCreateLandlordAccount === false) {
      console.log('All grants are open for applications alosed');
      setLandlord(false);
    } else {
      setLandlord(true);
    }
    setSmeCarParkClaims(deadlines?.stopToCreateSmeAccount);
    setPmcCarParkClaims(deadlines?.stopToCreatePmcAccount);
    setInstallerAccount(deadlines?.stopToCreateInstallerAccount);
  };
  const accountType = () => {
    chooseAccountType({ accountType: getAccountTypeSelectedLocal })
      .then(() => {
        setHasError(false);
        if (getAccountTypeSelectedLocal === 'Property Management Company') {
          window.location.assign('/evcg/pmcReg/create-account');
        } else if (
          getAccountTypeSelectedLocal === 'Small to medium enterprise'
        ) {
          history.push('/sme-reg/create-account');
        } else {
          window.location.assign('/evcg/landlordReg/create-account');
        }
      })
      .catch(() => {
        setHasError(true);
      });
  };

  const newRegistration = () => {
    signInRegistration()
      .then(() => {
        accountType();
      })
      .catch(() => {
        setHasError(true);
      });
  };

  const handleSelection = (event) => {
    setAccountTypeSelectedLocal(event.target.value);
  };

  const handleBtnClick = () => {
    dispatch(setAccountTypeSelected(getAccountTypeSelectedLocal));
    if (getAccountTypeSelectedLocal === 'Installer') {
      window.location.assign('/evcg/installer/installer-account-guidance');
    } else {
      newRegistration();
    }
  };

  let optionalAccountTypes = [];
  if (process.env.REACT_APP_SHOW_OPTIONAL_ACCOUNT_TYPES === 'true') {
    optionalAccountTypes = [
      {
        children: (
          <span key="0" className="govuk-!-margin-bottom-1">
            {copyProvider.getCopy(
              'SMEReg.AccountType.s8',
              'Property management company'
            )}
          </span>
        ),
        hint: {
          children: (
            <span key="0">
              {pmcCarParkClaims
                ? copyProvider.getCopy(
                    'SMEReg.AccountType.s9',
                    'You are the representative or managing agent of one or more landlords applying on behalf of your clients.'
                  )
                : 'Registration no longer available for this account type'}
            </span>
          ),
        },
        value: 'Property Management Company',
        disabled: !pmcCarParkClaims,
      },
      {
        children: (
          <span key="0" className="govuk-!-margin-bottom-1">
            {copyProvider.getCopy(
              'SMEReg.AccountType.s10',
              'Small to medium enterprise (SME)'
            )}
          </span>
        ),
        hint: {
          children: (
            <span key="0">
              {smeCarParkClaims
                ? copyProvider.getCopy(
                    'SMEReg.AccountType.s11',
                    'You are the owner of an SME applying to install infrastructure and chargepoints for staff or fleet use.'
                  )
                : 'Registration no longer available for this account type'}
            </span>
          ),
        },
        value: 'Small to medium enterprise',
        disabled: !smeCarParkClaims,
      },
    ];
  }
  useEffect(() => {
    const fetchWordingTemplates = async () => {
      try {
        const configurableDeadlines = await getConfigurableDeadlines();
        console.log('Configurable deadlines data:', configurableDeadlines);
        setIsLoading(true);
        if (!configurableDeadlines?.data) {
          setIsLoading(false);
          console.log('Configurable deadlines data is undefined');
        }
        await allGrantsInPastDates(
          configurableDeadlines.data.stopAccountsCreation
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchWordingTemplates();
  }, []);
  const pageModel = (
    <>
      <EnvironmentWatermark />
      <CommonAccountType
        summary={{
          title: copyProvider.getCopy(
            'Common.error-summary',
            'There is a problem'
          ),
        }}
        legendTitle={legendTitle}
        items={[
          {
            children: (
              <span key="0" className="govuk-!-margin-bottom-1">
                {copyProvider.getCopy('SMEReg.AccountType.s3', 'Landlord')}
              </span>
            ),
            hint: {
              children: (
                <div className="govuk-hint govuk-radios__hint">
                  {landlord ? (
                    <>
                      {copyProvider.getCopy(
                        'SMEReg.AccountType.s4',
                        'You are:'
                      )}
                      <ul className="govuk-list govuk-list--bullet govuk-hint">
                        <li>
                          {copyProvider.getCopy(
                            'SMEReg.AccountType.s5',
                            'The freeholder or leaseholder applying for properties you own or let'
                          )}
                        </li>
                        <li>
                          {copyProvider.getCopy(
                            'SMEReg.AccountType.s6',
                            'leaseholders applying as a Right to Manage company (RTM)'
                          )}
                        </li>
                        <li>
                          {copyProvider.getCopy(
                            'SMEReg.AccountType.s7',
                            'or a property factor in Scotland'
                          )}
                        </li>
                      </ul>
                    </>
                  ) : (
                    'Registration no longer available for this account type.'
                  )}
                </div>
              ),
            },
            value: 'Landlord',
            disabled: !landlord,
          },
          ...optionalAccountTypes,
          {
            children: (
              <span key="1" className="govuk-!-margin-bottom-1">
                {copyProvider.getCopy('SMEReg.AccountType.s12', 'Installer')}
              </span>
            ),
            hint: {
              children: (
                <span key="1">
                  {installerAccount
                    ? copyProvider.getCopy(
                        'SMEReg.AccountType.s13',
                        'You are the installer whose company is responsible for installing the chargepoint.'
                      )
                    : 'Registration no longer available for this account type.'}
                </span>
              ),
            },
            value: 'Installer',
            disabled: !installerAccount,
          },
        ]}
        onChange={handleSelection}
        value={getAccountTypeSelectedLocal}
        btnText={copyProvider.getCopy('SMEReg.AccountType.s14', 'Continue')}
        onClickContinue={handleBtnClick}
        errorMessage={errorMessage}
        serviceError={hasError}
      />
    </>
  );
  if (isLoading) {
    return (
      <div className="govuk-grid-row">
        {' '}
        <p className="govuk-body">Loading......</p>{' '}
      </div>
    );
  }
  return pageModel;
};

export default AccountType;
